import { useMemo } from 'react'
import { cn, formatCurrency, formatCompactCurrency, formatDate } from '@/lib/utils'
import { APP_IDENTITY, appFullName } from '@/config/appIdentity'
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts'
import {
  Shield, AlertTriangle, TrendingUp, TrendingDown,
  DollarSign, Calendar, Users, Target, CheckCircle2,
  XCircle, Clock, ArrowRight, Zap, Activity,
  FileText, ChevronRight, AlertOctagon,
} from 'lucide-react'

// ──────────────────────────────────────────────────────────────
// Types for the status report data payload
// ──────────────────────────────────────────────────────────────

export interface StatusReportData {
  generatedAt: string
  project: {
    id: string
    name: string
    code: string
    status: string
    healthStatus: string
    priority: string
    projectManager: string
    startDate: string
    plannedEndDate: string
    percentComplete: number
    schedulingMethod: string
  }
  overallHealth: 'Green' | 'Yellow' | 'Red'
  executiveSummary: string

  schedule: {
    spi: number
    scheduleVarianceDays: number
    percentComplete: number
    criticalMilestones: Array<{ name: string; date: string; status: 'complete' | 'on-track' | 'at-risk' | 'late' }>
    tasksTotal: number
    tasksComplete: number
    tasksInProgress: number
    tasksOverdue: number
  }

  cost: {
    cpi: number
    budgetTotal: number
    budgetSpent: number
    budgetCommitted: number
    budgetAvailable: number
    burnRate: number
    baselineBac: number
    eacTypical: number
    eacOptimistic: number
    eacPessimistic: number
    varianceAtCompletion: number
    costVariance: number
    tcpi: number
  }

  earnedValue: {
    bcws: number
    bcwp: number
    acwp: number
    sv: number
    cv: number
    spiTrend: Array<{ period: string; spi: number; cpi: number }>
    sCurve: Array<{ period: string; pv: number; ev: number; ac: number }>
  }

  risks: {
    total: number
    high: number
    medium: number
    low: number
    totalExposure: number
    topRisks: Array<{
      code: string
      title: string
      score: number
      costExposure: number
      status: string
      category: string
    }>
  }

  resources: {
    totalAssigned: number
    avgUtilization: number
    overallocated: number
    byRole: Array<{ role: string; count: number; utilization: number }>
  }

  forecast: {
    eacCost: number
    etcCost: number
    actualCost: number
    baselineBac: number
    varianceToBac: number
    eacRevenue: number
    eacMarginPercent: number
  }

  pendingChanges: {
    total: number
    items: Array<{ type: string; description: string; status: string; impact: string }>
  }

  aiNarrative: {
    summary: string
    keyFindings: string[]
    immediateActions: Array<{ priority: 'critical' | 'high' | 'medium'; action: string; owner: string; dueDate: string }>
    recommendations: Array<{ area: string; recommendation: string; impact: string }>
    outlook: string
  }
}

interface Props {
  data: StatusReportData
}

// ──────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────

const HEALTH_COLORS: Record<string, string> = {
  Green: '#16a34a',
  Yellow: '#ca8a04',
  Red: '#dc2626',
}

const HEALTH_BG: Record<string, string> = {
  Green: 'bg-emerald-50 border-emerald-200',
  Yellow: 'bg-amber-50 border-amber-200',
  Red: 'bg-red-50 border-red-200',
}

const HEALTH_TEXT: Record<string, string> = {
  Green: 'text-emerald-700',
  Yellow: 'text-amber-700',
  Red: 'text-red-700',
}

function HealthBadge({ status }: { status: string }) {
  return (
    <span className={cn(
      'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border',
      HEALTH_BG[status] ?? 'bg-gray-50 border-gray-200',
      HEALTH_TEXT[status] ?? 'text-gray-700',
    )}>
      <span
        className="w-2 h-2 rounded-full"
        style={{ backgroundColor: HEALTH_COLORS[status] ?? '#6b7280' }}
      />
      {status}
    </span>
  )
}

function MetricGauge({ label, value, threshold, unit, inverse }: {
  label: string
  value: number
  threshold?: { green: number; yellow: number }
  unit?: string
  inverse?: boolean
}) {
  const t = threshold ?? { green: 0.95, yellow: 0.85 }
  const zone = inverse
    ? (value <= t.green ? 'green' : value <= t.yellow ? 'yellow' : 'red')
    : (value >= t.green ? 'green' : value >= t.yellow ? 'yellow' : 'red')

  const zoneColors = {
    green: { bg: 'bg-emerald-50', ring: 'ring-emerald-300', text: 'text-emerald-700', fill: '#059669' },
    yellow: { bg: 'bg-amber-50', ring: 'ring-amber-300', text: 'text-amber-700', fill: '#d97706' },
    red: { bg: 'bg-red-50', ring: 'ring-red-300', text: 'text-red-700', fill: '#dc2626' },
  }
  const c = zoneColors[zone]

  // Arc gauge
  const gaugeMin = 0.5
  const gaugeMax = 1.5
  const clamped = Math.max(gaugeMin, Math.min(gaugeMax, value))
  const angle = ((clamped - gaugeMin) / (gaugeMax - gaugeMin)) * 180
  const cx = 50, cy = 45, r = 35
  const startRad = (-90 * Math.PI) / 180
  const endRad = ((-90 + angle) * Math.PI) / 180
  const x1 = cx + r * Math.cos(startRad)
  const y1 = cy + r * Math.sin(startRad)
  const x2 = cx + r * Math.cos(endRad)
  const y2 = cy + r * Math.sin(endRad)
  const largeArc = angle > 180 ? 1 : 0

  return (
    <div className={cn('rounded-xl p-3 ring-1', c.bg, c.ring)}>
      <div className="text-[10px] font-medium text-text-secondary uppercase tracking-wider mb-1">{label}</div>
      <div className="flex items-center gap-3">
        <svg width="60" height="36" viewBox="0 0 100 55">
          <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
            fill="none" stroke="#e5e7eb" strokeWidth="8" strokeLinecap="round" />
          <path d={`M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`}
            fill="none" stroke={c.fill} strokeWidth="8" strokeLinecap="round" />
        </svg>
        <div>
          <div className={cn('text-xl font-bold font-display', c.text)}>
            {value.toFixed(2)}{unit ?? ''}
          </div>
        </div>
      </div>
    </div>
  )
}

function SectionHeader({ icon, title, subtitle }: {
  icon: React.ReactNode
  title: string
  subtitle?: string
}) {
  return (
    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border">
      <div className="w-7 h-7 rounded-lg bg-brand-50 flex items-center justify-center text-brand-600">
        {icon}
      </div>
      <div>
        <h3 className="text-heading-sm font-display">{title}</h3>
        {subtitle && <p className="text-[11px] text-text-tertiary">{subtitle}</p>}
      </div>
    </div>
  )
}

function StatCard({ label, value, subValue, icon, color }: {
  label: string
  value: string | number
  subValue?: string
  icon: React.ReactNode
  color: 'brand' | 'green' | 'yellow' | 'red' | 'blue'
}) {
  const colorMap = {
    brand: 'bg-brand-50 text-brand-700',
    green: 'bg-emerald-50 text-emerald-700',
    yellow: 'bg-amber-50 text-amber-700',
    red: 'bg-red-50 text-red-700',
    blue: 'bg-blue-50 text-blue-700',
  }
  return (
    <div className="bg-white rounded-lg border border-border p-3">
      <div className="flex items-center gap-2 mb-1">
        <div className={cn('w-6 h-6 rounded flex items-center justify-center', colorMap[color])}>
          {icon}
        </div>
        <span className="text-[10px] uppercase tracking-wider text-text-secondary font-medium">{label}</span>
      </div>
      <div className="text-lg font-bold font-display">{value}</div>
      {subValue && <div className="text-[11px] text-text-tertiary">{subValue}</div>}
    </div>
  )
}

function ProgressBar({ value, max, color, label }: {
  value: number
  max: number
  color: string
  label?: string
}) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0
  return (
    <div>
      {label && <div className="flex justify-between text-[11px] mb-0.5">
        <span className="text-text-secondary">{label}</span>
        <span className="font-medium">{pct.toFixed(0)}%</span>
      </div>}
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  )
}

const PIE_COLORS = ['#dc2626', '#ca8a04', '#16a34a', '#6081ad']

// ──────────────────────────────────────────────────────────────
// Main Component
// ──────────────────────────────────────────────────────────────

export function StatusReportView({ data }: Props) {
  const budgetPct = data.cost.budgetTotal > 0
    ? (data.cost.budgetSpent / data.cost.budgetTotal) * 100
    : 0

  const riskPieData = useMemo(() => [
    { name: 'High', value: data.risks.high },
    { name: 'Medium', value: data.risks.medium },
    { name: 'Low', value: data.risks.low },
  ].filter(d => d.value > 0), [data.risks])

  const taskCompletionData = useMemo(() => [
    { name: 'Complete', value: data.schedule.tasksComplete, fill: '#16a34a' },
    { name: 'In Progress', value: data.schedule.tasksInProgress, fill: '#6081ad' },
    { name: 'Overdue', value: data.schedule.tasksOverdue, fill: '#dc2626' },
    { name: 'Remaining', value: Math.max(0, data.schedule.tasksTotal - data.schedule.tasksComplete - data.schedule.tasksInProgress - data.schedule.tasksOverdue), fill: '#e5e7eb' },
  ].filter(d => d.value > 0), [data.schedule])

  const eacComparisonData = useMemo(() => [
    { name: 'BAC', value: data.cost.baselineBac, fill: '#6081ad' },
    { name: 'EAC Best', value: data.cost.eacOptimistic, fill: '#16a34a' },
    { name: 'EAC Likely', value: data.cost.eacTypical, fill: '#ca8a04' },
    { name: 'EAC Worst', value: data.cost.eacPessimistic, fill: '#dc2626' },
  ], [data.cost])

  return (
    <div className="space-y-5 pb-4">
      {/* ── Report Header ── */}
      <div className={cn(
        'rounded-xl border-2 p-5',
        HEALTH_BG[data.overallHealth] ?? 'bg-gray-50 border-gray-200',
      )}>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <FileText size={18} className="text-brand-600" />
              <span className="text-[10px] uppercase tracking-wider font-medium text-text-secondary">
                AI-Generated Status Report
              </span>
            </div>
            <h2 className="text-heading-md font-display">{data.project.name}</h2>
            <div className="flex items-center gap-3 mt-1 text-body-sm text-text-secondary">
              <span>{data.project.code}</span>
              <span>&#183;</span>
              <span>PM: {data.project.projectManager}</span>
              <span>&#183;</span>
              <span>{formatDate(data.generatedAt)}</span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <HealthBadge status={data.overallHealth} />
            <span className="text-[10px] text-text-tertiary">
              {data.project.priority} Priority
            </span>
          </div>
        </div>

        {/* Executive summary */}
        <div className="mt-4 bg-white/80 rounded-lg p-3 border border-white/50">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-text-secondary mb-1">Executive Summary</div>
          <p className="text-body-sm leading-relaxed">{data.aiNarrative.summary}</p>
        </div>
      </div>

      {/* ── Key Metrics Row ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricGauge label="SPI" value={data.schedule.spi} />
        <MetricGauge label="CPI" value={data.cost.cpi} />
        <MetricGauge label="TCPI" value={data.cost.tcpi} threshold={{ green: 1.05, yellow: 1.1 }} inverse />
        <StatCard
          label="% Complete"
          value={`${data.schedule.percentComplete}%`}
          subValue={`${data.schedule.scheduleVarianceDays} days ${data.schedule.scheduleVarianceDays >= 0 ? 'ahead' : 'behind'}`}
          icon={<Target size={14} />}
          color={data.schedule.percentComplete >= 50 ? 'green' : 'yellow'}
        />
      </div>

      {/* ── S-Curve / Performance Trend ── */}
      {data.earnedValue.sCurve.length > 0 && (
        <div className="bg-white rounded-xl border border-border p-4">
          <SectionHeader icon={<Activity size={14} />} title="Earned Value S-Curve" subtitle="Planned vs Earned vs Actual Cost" />
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.earnedValue.sCurve} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="pvGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6081ad" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#6081ad" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="evGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#16a34a" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="acGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#dc2626" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#dc2626" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="period" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1e6).toFixed(1)}M`} />
              <Tooltip
                formatter={(value: number) => formatCurrency(value)}
                contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e7eb' }}
              />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="pv" name="PV (BCWS)" stroke="#6081ad" fill="url(#pvGrad)" strokeWidth={2} />
              <Area type="monotone" dataKey="ev" name="EV (BCWP)" stroke="#16a34a" fill="url(#evGrad)" strokeWidth={2} />
              <Area type="monotone" dataKey="ac" name="AC (ACWP)" stroke="#dc2626" fill="url(#acGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── SPI / CPI Trend ── */}
      {data.earnedValue.spiTrend.length > 0 && (
        <div className="bg-white rounded-xl border border-border p-4">
          <SectionHeader icon={<TrendingUp size={14} />} title="Performance Index Trend" subtitle="SPI & CPI over time" />
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={data.earnedValue.spiTrend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="period" tick={{ fontSize: 10 }} />
              <YAxis domain={[0.7, 1.3]} tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e7eb' }} />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              {/* Threshold reference line at 1.0 */}
              <Line type="monotone" dataKey={() => 1} name="Target" stroke="#9ca3af" strokeDasharray="4 4" strokeWidth={1} dot={false} />
              <Line type="monotone" dataKey="spi" name="SPI" stroke="#6081ad" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="cpi" name="CPI" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Budget & Cost ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Budget breakdown */}
        <div className="bg-white rounded-xl border border-border p-4">
          <SectionHeader icon={<DollarSign size={14} />} title="Budget Overview" subtitle={`${budgetPct.toFixed(0)}% utilized`} />
          <div className="space-y-3">
            <ProgressBar value={data.cost.budgetSpent} max={data.cost.budgetTotal} color="#dc2626" label="Spent" />
            <ProgressBar value={data.cost.budgetCommitted} max={data.cost.budgetTotal} color="#ca8a04" label="Committed" />
            <ProgressBar value={data.cost.budgetAvailable} max={data.cost.budgetTotal} color="#16a34a" label="Available" />
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            <div className="text-center p-2 rounded-lg bg-surface-muted">
              <div className="text-[10px] text-text-secondary">Total</div>
              <div className="font-bold text-sm">{formatCompactCurrency(data.cost.budgetTotal)}</div>
            </div>
            <div className="text-center p-2 rounded-lg bg-surface-muted">
              <div className="text-[10px] text-text-secondary">Burn Rate</div>
              <div className="font-bold text-sm">{formatCompactCurrency(data.cost.burnRate)}/mo</div>
            </div>
          </div>
        </div>

        {/* EAC Scenarios */}
        <div className="bg-white rounded-xl border border-border p-4">
          <SectionHeader icon={<Target size={14} />} title="EAC Scenarios" subtitle="Estimate at Completion range" />
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={eacComparisonData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1e6).toFixed(1)}M`} />
              <Tooltip formatter={(value: number) => formatCompactCurrency(value)} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {eacComparisonData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="flex items-center justify-between text-[11px] mt-2 px-1">
            <span className="text-text-secondary">VAC: <span className={data.cost.varianceAtCompletion >= 0 ? 'text-emerald-600 font-medium' : 'text-red-600 font-medium'}>
              {formatCompactCurrency(Math.abs(data.cost.varianceAtCompletion))} {data.cost.varianceAtCompletion >= 0 ? 'under' : 'over'}
            </span></span>
            <span className="text-text-secondary">CV: <span className={data.cost.costVariance >= 0 ? 'text-emerald-600 font-medium' : 'text-red-600 font-medium'}>
              {formatCompactCurrency(Math.abs(data.cost.costVariance))}
            </span></span>
          </div>
        </div>
      </div>

      {/* ── Schedule & Tasks ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Task completion */}
        <div className="bg-white rounded-xl border border-border p-4">
          <SectionHeader icon={<Calendar size={14} />} title="Schedule Status" subtitle={`${data.schedule.tasksTotal} tasks tracked`} />
          <div className="flex items-center gap-4">
            <div className="w-28 h-28 flex-shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={taskCompletionData} cx="50%" cy="50%" innerRadius={28} outerRadius={45} dataKey="value" paddingAngle={2}>
                    {taskCompletionData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-1.5 flex-1">
              {taskCompletionData.map(d => (
                <div key={d.name} className="flex items-center gap-2 text-[11px]">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: d.fill }} />
                  <span className="text-text-secondary flex-1">{d.name}</span>
                  <span className="font-medium">{d.value}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Milestones */}
          {data.schedule.criticalMilestones.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-2">Key Milestones</div>
              <div className="space-y-1.5">
                {data.schedule.criticalMilestones.map((m, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px]">
                    {m.status === 'complete' && <CheckCircle2 size={12} className="text-emerald-600" />}
                    {m.status === 'on-track' && <Clock size={12} className="text-blue-600" />}
                    {m.status === 'at-risk' && <AlertTriangle size={12} className="text-amber-600" />}
                    {m.status === 'late' && <XCircle size={12} className="text-red-600" />}
                    <span className="flex-1 truncate">{m.name}</span>
                    <span className="text-text-tertiary">{formatDate(m.date)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Risk matrix */}
        <div className="bg-white rounded-xl border border-border p-4">
          <SectionHeader icon={<Shield size={14} />} title="Risk Profile" subtitle={`${data.risks.total} active risks`} />
          <div className="flex items-center gap-4">
            <div className="w-28 h-28 flex-shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={riskPieData} cx="50%" cy="50%" innerRadius={28} outerRadius={45} dataKey="value" paddingAngle={2}>
                    {riskPieData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-2">
              <div className="flex items-center justify-between text-[11px]">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-red-600" />High</span>
                <span className="font-medium">{data.risks.high}</span>
              </div>
              <div className="flex items-center justify-between text-[11px]">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-yellow-600" />Medium</span>
                <span className="font-medium">{data.risks.medium}</span>
              </div>
              <div className="flex items-center justify-between text-[11px]">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-green-600" />Low</span>
                <span className="font-medium">{data.risks.low}</span>
              </div>
              <div className="pt-1 border-t border-border text-[11px]">
                <span className="text-text-secondary">Total Exposure: </span>
                <span className="font-semibold text-red-600">{formatCompactCurrency(data.risks.totalExposure)}</span>
              </div>
            </div>
          </div>
          {/* Top risks */}
          {data.risks.topRisks.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-2">Top Risks</div>
              <div className="space-y-1.5">
                {data.risks.topRisks.slice(0, 3).map((r, i) => (
                  <div key={i} className="flex items-start gap-2 text-[11px]">
                    <AlertTriangle size={12} className={r.score > 0.6 ? 'text-red-600' : 'text-amber-600'} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{r.code}: {r.title}</div>
                      <div className="text-text-tertiary">Score: {(r.score * 100).toFixed(0)}% &#183; Exposure: {formatCompactCurrency(r.costExposure)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Resources ── */}
      {data.resources.byRole.length > 0 && (
        <div className="bg-white rounded-xl border border-border p-4">
          <SectionHeader icon={<Users size={14} />} title="Resource Utilization" subtitle={`${data.resources.totalAssigned} resources assigned, ${data.resources.avgUtilization}% avg utilization`} />
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={data.resources.byRole} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="role" tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(value: number) => `${value}%`} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              <Bar dataKey="utilization" name="Utilization" radius={[4, 4, 0, 0]}>
                {data.resources.byRole.map((entry, i) => (
                  <Cell key={i} fill={entry.utilization > 95 ? '#dc2626' : entry.utilization > 85 ? '#ca8a04' : '#16a34a'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          {data.resources.overallocated > 0 && (
            <div className="mt-2 flex items-center gap-2 text-[11px] text-red-600 bg-red-50 rounded-lg px-3 py-1.5">
              <AlertTriangle size={12} />
              <span>{data.resources.overallocated} resource(s) overallocated (&gt;100% utilization)</span>
            </div>
          )}
        </div>
      )}

      {/* ── Forecast Summary ── */}
      {data.forecast.eacCost > 0 && (
        <div className="bg-white rounded-xl border border-border p-4">
          <SectionHeader icon={<TrendingUp size={14} />} title="Forecast Summary" subtitle="Current EAC forecast vs baseline" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard label="EAC Cost" value={formatCompactCurrency(data.forecast.eacCost)} icon={<DollarSign size={12} />} color="brand" />
            <StatCard label="ETC Remaining" value={formatCompactCurrency(data.forecast.etcCost)} icon={<Clock size={12} />} color="blue" />
            <StatCard label="Variance to BAC" value={formatCompactCurrency(Math.abs(data.forecast.varianceToBac))}
              subValue={data.forecast.varianceToBac >= 0 ? 'Under budget' : 'Over budget'}
              icon={data.forecast.varianceToBac >= 0 ? <TrendingDown size={12} /> : <TrendingUp size={12} />}
              color={data.forecast.varianceToBac >= 0 ? 'green' : 'red'}
            />
            <StatCard label="EAC Margin" value={`${data.forecast.eacMarginPercent.toFixed(1)}%`}
              subValue={`Revenue: ${formatCompactCurrency(data.forecast.eacRevenue)}`}
              icon={<Target size={12} />}
              color={data.forecast.eacMarginPercent >= 10 ? 'green' : data.forecast.eacMarginPercent >= 5 ? 'yellow' : 'red'}
            />
          </div>
        </div>
      )}

      {/* ── Pending Changes ── */}
      {data.pendingChanges.total > 0 && (
        <div className="bg-white rounded-xl border border-border p-4">
          <SectionHeader icon={<FileText size={14} />} title="Pending Changes" subtitle={`${data.pendingChanges.total} open items`} />
          <div className="space-y-2">
            {data.pendingChanges.items.map((item, i) => (
              <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-surface-muted text-[11px]">
                <ChevronRight size={12} className="text-brand-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium">{item.type}:</span> {item.description}
                  <div className="text-text-tertiary mt-0.5">Status: {item.status} &#183; Impact: {item.impact}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── AI Key Findings ── */}
      <div className="bg-gradient-to-br from-brand-50 to-white rounded-xl border border-brand-200 p-4">
        <SectionHeader icon={<Zap size={14} />} title="AI Key Findings" subtitle={`Powered by ${APP_IDENTITY.name} AI`} />
        <div className="space-y-2">
          {data.aiNarrative.keyFindings.map((finding, i) => (
            <div key={i} className="flex items-start gap-2 text-body-sm">
              <span className="w-5 h-5 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">
                {i + 1}
              </span>
              <span>{finding}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Immediate Actions ── */}
      <div className="bg-white rounded-xl border-2 border-amber-200 p-4">
        <SectionHeader icon={<AlertOctagon size={14} />} title="Immediate Actions Required" subtitle="Prioritized action items" />
        <div className="space-y-2">
          {data.aiNarrative.immediateActions.map((action, i) => {
            const priorityConfig = {
              critical: { bg: 'bg-red-100 text-red-800 border-red-300', icon: <XCircle size={12} /> },
              high: { bg: 'bg-amber-100 text-amber-800 border-amber-300', icon: <AlertTriangle size={12} /> },
              medium: { bg: 'bg-blue-100 text-blue-800 border-blue-300', icon: <Clock size={12} /> },
            }
            const pc = priorityConfig[action.priority]
            return (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg border bg-white">
                <div className={cn('px-2 py-0.5 rounded text-[10px] font-bold uppercase border flex items-center gap-1', pc.bg)}>
                  {pc.icon}
                  {action.priority}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-body-sm font-medium">{action.action}</div>
                  <div className="flex items-center gap-3 text-[11px] text-text-tertiary mt-1">
                    <span className="flex items-center gap-1"><Users size={10} /> {action.owner}</span>
                    <span className="flex items-center gap-1"><Calendar size={10} /> {action.dueDate}</span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* ── Recommendations ── */}
      <div className="bg-white rounded-xl border border-border p-4">
        <SectionHeader icon={<Target size={14} />} title="Recommendations" subtitle="Strategic guidance from AI analysis" />
        <div className="space-y-3">
          {data.aiNarrative.recommendations.map((rec, i) => (
            <div key={i} className="p-3 rounded-lg bg-gradient-to-r from-surface-muted to-white border border-border">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-600 bg-brand-50 px-2 py-0.5 rounded">
                  {rec.area}
                </span>
              </div>
              <div className="text-body-sm">{rec.recommendation}</div>
              <div className="text-[11px] text-text-tertiary mt-1 flex items-center gap-1">
                <ArrowRight size={10} className="text-brand-500" />
                <span>Expected impact: {rec.impact}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Outlook ── */}
      <div className="bg-gradient-to-br from-[#233145] to-[#3a5070] rounded-xl p-4 text-white">
        <div className="flex items-center gap-2 mb-2">
          <Activity size={14} className="text-amber-400" />
          <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-300">Project Outlook</span>
        </div>
        <p className="text-sm leading-relaxed opacity-95">{data.aiNarrative.outlook}</p>
        <div className="mt-3 flex items-center gap-2 text-[10px] text-white/60">
          <Zap size={10} className="text-amber-400" />
          <span>Generated by {appFullName()} Agent &#183; {formatDate(data.generatedAt)}</span>
        </div>
      </div>
    </div>
  )
}
